import React, { useState, useEffect, useRef } from "react";
import {
  AccommodationListing,
  HotelInitial,
  ViewLodging,
  RoomDetails,
} from "@/types/accommodation";
import { motion } from "framer-motion";
import { Location } from "@/types/trip";
import ResponsiveHotelImages from "./ResponsiveHotelImages";
import {
  useLodgingDetails,
  useRoomDetails,
} from "@/lib/accommodation/agodaHotels";
import {
  formatImageUrl,
  toTitleCase,
  formatPrice,
  formatReviewScore,
  formatReviewCount,
} from "@/lib/general";
import LazyLoad from "../../image/LazyLoad";
import FacilitiesModal from "./FacilitiesModal";
import { iconMap } from "@/utils/tripBuilder/accommodation";
import {
  InfoIcon,
  BedIcon,
  CheckCircle2,
  UserIcon,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import useAccommodationStore from "@/stores/tripBuilder/useAccommodation";
import useCurrencyStore from "@/stores/ui/currencyStore";
import RoomDetailsModal from "./RoomDetailsModal";
import plumGuideLogo from "@/assets/logos/external/plum-guide-horizontal-logo.svg";
import { useToast } from "@/hooks/use-toast";

interface MainLodgingModalProps {
  lodging: ViewLodging;
  coreLodging: HotelInitial | AccommodationListing | null;
  setIsOpen: (isOpen: boolean) => void;
  checkIn: string;
  checkOut: string;
  location: Location;
  handleAddLodgingModal: (
    viewLodging: ViewLodging,
    lodging: HotelInitial | AccommodationListing
  ) => void;
  selectedRoom: RoomDetails | null;
  setSelectedRoom: (room: RoomDetails | null) => void;
  isAdmin: boolean;
}

const LoadingSkeleton = () => {
  return (
    <div className="w-full h-full animate-pulse overflow-y-auto no-scrollbar">
      {/* Header Skeleton */}
      <div className="flex justify-between items-start py-3">
        <div className="space-y-2">
          <div className="h-6 w-64 bg-neutral-200 rounded-md"></div>
          <div className="h-4 w-48 bg-neutral-200 rounded-md"></div>
        </div>
        <div className="h-8 w-20 bg-neutral-200 rounded-full"></div>
      </div>

      {/* Image Gallery Skeleton */}
      <div className="grid grid-cols-4 gap-2 mb-6">
        <div className="col-span-2 row-span-2 h-80 bg-neutral-200 rounded-lg"></div>
        {[...Array(4)].map((_, index) => (
          <div
            key={index}
            className="h-[156px] bg-neutral-200 rounded-lg"
          ></div>
        ))}
      </div>

      {/* Description Skeleton */}
      <div className="mb-8">
        <div className="h-7 w-32 bg-neutral-200 rounded-md mb-3"></div>
        <div className="space-y-2">
          <div className="h-4 w-full bg-neutral-200 rounded-md"></div>
          <div className="h-4 w-full bg-neutral-200 rounded-md"></div>
          <div className="h-4 w-3/4 bg-neutral-200 rounded-md"></div>
        </div>
      </div>

      {/* Facilities Skeleton */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-3">
          <div className="h-7 w-32 bg-neutral-200 rounded-md"></div>
          <div className="h-8 w-20 bg-neutral-200 rounded-full"></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="flex items-center gap-3">
              <div className="w-8 h-8 bg-neutral-200 rounded-full"></div>
              <div className="h-4 w-32 bg-neutral-200 rounded-md"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const RoomLoadingSkeleton = () => {
  return (
    <>
      {/* Header Skeleton */}
      <div className="flex flex-row items-start justify-between py-3">
        <div className="sticky top-0 z-10 pb-2">
          <div className="flex flex-col items-start justify-between space-y-0.5">
            <div className="h-7 w-48 bg-neutral-200 rounded-md"></div>
            <div className="flex items-center gap-1.5 mt-1">
              <div className="h-4 w-32 bg-neutral-200 rounded-md"></div>
              <div className="w-0.5 h-0.5 bg-neutral-200 rounded-full"></div>
              <div className="h-4 w-36 bg-neutral-200 rounded-md"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Rooms Skeleton */}
      <div className="relative">
        <div className="flex flex-row gap-4">
          {[...Array(2)].map((_, index) => (
            <div
              key={index}
              className="flex-none w-64 snap-start h-fit animate-pulse"
            >
              <div className="flex flex-col gap-2">
                {/* Image Skeleton */}
                <div className="relative w-full h-32 bg-neutral-200 rounded-lg">
                  <div className="absolute top-2 right-2 w-20 h-7 bg-white/60 backdrop-blur-sm rounded-lg"></div>
                </div>

                {/* Content Skeleton */}
                <div className="h-5 w-[90%] bg-neutral-200 rounded-md"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

const MainLodgingModal: React.FC<MainLodgingModalProps> = ({
  location,
  lodging,
  coreLodging,
  setIsOpen,
  checkIn,
  checkOut,
  handleAddLodgingModal,
  selectedRoom,
  setSelectedRoom,
  isAdmin,
}) => {
  const { selectedCurrency } = useCurrencyStore();
  const { data: lodgingDetails, isLoading: isLodgingDetailsLoading } =
    useLodgingDetails(
      lodging.type,
      lodging.id,
      checkIn,
      checkOut,
      selectedCurrency.code
    );

  const {
    data: roomDetails,
    isLoading: isRoomDetailsLoading,
    error: roomDetailsError,
  } = useRoomDetails(
    lodging.id,
    checkIn,
    checkOut,
    selectedCurrency.code,
    lodging.type
  );

  const { toast } = useToast();

  const [isAddingToTrip, setIsAddingToTrip] = useState(false);
  const [showFacilities, setShowFacilities] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { setViewLodging } = useAccommodationStore();
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showAllHomeTruths, setShowAllHomeTruths] = useState(false);

  // Group facilities by category (example implementation)
  const groupedFacilities = {
    essential: (lodgingDetails?.facilities ?? []).slice(0, 6),
    all: lodgingDetails?.facilities ?? [],
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const checkScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10); // 10px buffer
    };

    checkScroll();
    container.addEventListener("scroll", checkScroll);
    return () => container.removeEventListener("scroll", checkScroll);
  }, [roomDetails]);

  const scroll = (direction: "left" | "right") => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Calculate the width of the container and adjust scroll amount for 2 cards
    const containerWidth = container.clientWidth;
    const scrollAmount = containerWidth / 2; // This will be half the container width (1 card)

    const scrollLeft =
      container.scrollLeft +
      (direction === "left" ? -scrollAmount : scrollAmount);
    container.scrollTo({
      left: scrollLeft,
      behavior: "smooth",
    });
  };

  const getFirstParagraph = (text: string) => {
    const paragraphs = text.split("\n\n");
    return paragraphs.slice(0, 2).join("\n\n");
  };

  useEffect(() => {
    if (lodgingDetails?.unavailable) {
      toast({
        variant: "warning",
        title: "Lodging Unavailable",
        description:
          "This lodging is currently unavailable for the selected dates.",
      });
      setIsOpen(false);
      setViewLodging(null);
    }
  }, [lodgingDetails, setIsOpen, setViewLodging, toast]);

  const handleAddToTrip = () => {
    if (!coreLodging) return;
    setIsAddingToTrip(true);
    handleAddLodgingModal(lodging, coreLodging);
    setTimeout(() => {
      setIsAddingToTrip(false);
    }, 1000);
  };

  const handleViewRoom = (room: RoomDetails) => {
    setSelectedRoom(room);
  };

  const handleCloseRoomDetails = () => {
    setSelectedRoom(null);
  };

  const handleAddRoom = (room: RoomDetails) => {
    if (!coreLodging) return;
    setSelectedRoom(room);
    handleAddLodgingModal(lodging, coreLodging);
    setSelectedRoom(null);
  };

  // Memoize bed type computation
  const formattedBedType = (bedTypes: { name: string }[]) => {
    const bedName = bedTypes[0]?.name;
    return toTitleCase(
      bedName === "unknown" ? "Unspecified" : `${bedName} bed` || "Unspecified"
    );
  };

  return (
    <>
      <motion.div
        className="absolute inset-0 bg-white/20 backdrop-blur-sm z-50 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="w-full h-full p-3 flex flex-col gap-1">
          <div className="bg-white/90 backdrop-blur-xl rounded-lg px-5 pb-2 flex flex-col border border-neutral-200 h-full">
            {isLodgingDetailsLoading ? (
              <LoadingSkeleton />
            ) : (
              <>
                {/* Header - Fixed height with floating effect */}
                <div className="flex flex-row justify-between gap-4 items-start py-3 mb-2">
                  <div className="sticky top-0 z-10 ">
                    <div className="flex flex-col items-start justify-between space-y-0.5">
                      {lodging.type === "home" && (
                        <div className="ring-[1.5px] ring-[#FDBB30] rounded-full px-3 py-2">
                          <img
                            src={plumGuideLogo}
                            alt={lodging.name}
                            className="opacity-80 w-20 object-cover"
                          />
                        </div>
                      )}
                      <h2 className="text-lg font-medium text-neutral-800 pt-1.5">
                        {lodging.name}
                      </h2>
                      {lodging.type === "hotel" ? (
                        <div className="flex flex-row items-center space-x-2">
                          <div className="py-1 bg-indigo-50 flex flex-row items-center space-x-2 px-2 text-indigo-600 rounded-md">
                            <i className="fa-solid fa-star text-indigo-600 text-[12px] -translate-y-[0.5px]"></i>
                            <p className="experience-name-text text-[13px] font-light text-indigo-600 truncate whitespace-nowrap">
                              {lodging.moreInfo &&
                                formatReviewScore(
                                  lodging.moreInfo.reviewScore || 0
                                )}
                            </p>
                          </div>
                          <p className="pl-0.5 experience-name-text text-[12px] font-light text-neutral-500 truncate whitespace-nowrap">
                            {lodging.moreInfo &&
                              formatReviewCount(
                                lodging.moreInfo.reviewCount || 0
                              )}{" "}
                            reviews
                          </p>
                        </div>
                      ) : (
                        <div className="flex flex-row items-center space-x-2">
                          <p className="text-[13px] text-neutral-600 font-light">
                            {(coreLodging as AccommodationListing)
                              ?.property_type || "Property"}{" "}
                            in{" "}
                            {(coreLodging as AccommodationListing)
                              ?.neighbourhood ||
                              (coreLodging as AccommodationListing)?.location ||
                              "Central Location"}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex-none flex flex-col items-center justify-start gap-2 ml-4">
                    <button
                      onClick={() => {
                        setIsOpen(false);
                        setViewLodging(null);
                      }}
                      className="text-xs text-neutral-600 hover:text-neutral-800 border border-neutral-200 hover:border-neutral-600 h-8 rounded-full transition-all duration-300 w-16 flex items-center justify-center gap-1"
                    >
                      <i className="fa-solid fa-xmark text-[11px]"></i>
                      Close
                    </button>
                    {coreLodging && isAdmin && (
                      <button
                        onClick={handleAddToTrip}
                        className="text-xs text-white bg-indigo-600 hover:bg-indigo-700 h-8 rounded-full transition-all duration-300 w-16 flex items-center justify-center gap-1"
                      >
                        {isAddingToTrip ? (
                          <i className="fa-solid fa-check text-[11px]"></i>
                        ) : (
                          <>
                            <i className="fa-solid fa-plus text-[11px]"></i>
                            <span>Add</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {/* Content Area - Scrollable */}
                <div className="flex-1 overflow-y-auto no-scrollbar rounded-lg">
                  <ResponsiveHotelImages
                    lodgingDetails={lodgingDetails}
                    lodging={lodging}
                  />

                  {/* Description */}
                  <div className="mb-8">
                    <h3 className="text-[15px] font-semibold mb-2 text-neutral-800">
                      Description
                    </h3>
                    <div className="text-neutral-700 font-light text-[13px] leading-relaxed">
                      {lodgingDetails?.description ? (
                        <>
                          <p className="whitespace-pre-line">
                            {showFullDescription
                              ? lodgingDetails.description
                              : getFirstParagraph(lodgingDetails.description)}
                          </p>
                          {lodgingDetails.description.includes("\n\n") && (
                            <button
                              onClick={() =>
                                setShowFullDescription(!showFullDescription)
                              }
                              className="text-indigo-600 hover:text-indigo-700 text-xs mt-2 transition-colors duration-200"
                            >
                              {showFullDescription ? "Show less" : "Show more"}
                            </button>
                          )}
                        </>
                      ) : (
                        <p>
                          {lodging.type === "hotel"
                            ? "This hotel is located in a prime location, offering comfortable accommodations for your stay."
                            : "This property offers a comfortable stay in a great location."}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Home Truths */}
                  {lodging.type === "home" &&
                    lodgingDetails?.homeTruths &&
                    lodgingDetails.homeTruths.length > 0 && (
                      <div className="mb-8">
                        <div className="flex justify-between items-center mb-3">
                          <h3 className="text-[15px] font-semibold text-neutral-800">
                            Home Truths
                          </h3>
                          {lodgingDetails.homeTruths.length > 3 && (
                            <button
                              onClick={() =>
                                setShowAllHomeTruths(!showAllHomeTruths)
                              }
                              className="text-xs text-neutral-600 hover:text-neutral-600 border border-neutral-200 hover:border-neutral-600 px-3 py-1.5 rounded-full transition-all duration-300"
                            >
                              {showAllHomeTruths ? "Show Less" : "View All"}
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-1 gap-3">
                          {(showAllHomeTruths
                            ? lodgingDetails.homeTruths
                            : lodgingDetails.homeTruths.slice(0, 3)
                          ).map((truth: string, index: number) => (
                            <div
                              key={index}
                              className="flex items-center gap-3 "
                            >
                              <div className="w-8 h-8 bg-neutral-200/50 rounded-full flex items-center justify-center shrink-0">
                                <InfoIcon className="w-3.5 h-3.5 text-neutral-600" />
                              </div>
                              <p className="text-neutral-700 font-light text-[13px] leading-relaxed">
                                {truth}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {/* Key Facilities */}
                  <div className="mb-8">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-[15px] font-semibold text-neutral-800">
                        Key Facilities
                      </h3>
                      {lodgingDetails?.facilities &&
                        lodgingDetails.facilities.length > 0 && (
                          <button
                            onClick={() => setShowFacilities(true)}
                            className="text-xs text-neutral-600 hover:text-neutral-600 border border-neutral-200 hover:border-neutral-600 px-3 py-1.5 rounded-full transition-all duration-300"
                          >
                            View All
                          </button>
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {groupedFacilities.essential &&
                      groupedFacilities.essential.length > 0 ? (
                        groupedFacilities.essential.map(
                          (facility: any, index: number) => {
                            const IconComponent =
                              iconMap[facility.icon as keyof typeof iconMap];

                            return (
                              <div
                                key={index}
                                className="flex items-center gap-3"
                              >
                                <div className="w-8 h-8 bg-neutral-200/50 rounded-full flex items-center justify-center">
                                  {IconComponent ? (
                                    <IconComponent className="w-3.5 h-3.5 text-neutral-600" />
                                  ) : (
                                    <CheckCircle2 className="w-3.5 h-3.5 text-neutral-600" />
                                  )}
                                </div>
                                <span className="text-neutral-700 font-light leading-relaxed text-[13px]">
                                  {facility.name}
                                </span>
                              </div>
                            );
                          }
                        )
                      ) : (
                        <>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-neutral-200/50 rounded-full flex items-center justify-center">
                              <CheckCircle2 className="w-3.5 h-3.5 text-neutral-600" />
                            </div>
                            <span className="text-neutral-700 font-light leading-relaxed text-[13px]">
                              {lodging.type === "hotel"
                                ? "Wi-Fi"
                                : "Essentials"}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-neutral-200/50 rounded-full flex items-center justify-center">
                              <CheckCircle2 className="w-3.5 h-3.5 text-neutral-600" />
                            </div>
                            <span className="text-neutral-700 font-light leading-relaxed text-[13px]">
                              {lodging.type === "hotel"
                                ? "Air Conditioning"
                                : "Kitchen"}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Rooms Section - Only show for hotels */}
                  {lodging.type === "hotel" && (
                    <div className="mb-8">
                      {isRoomDetailsLoading ? (
                        <RoomLoadingSkeleton />
                      ) : roomDetailsError ? (
                        <div className="flex flex-col items-center justify-center py-6 px-4 bg-neutral-50 rounded-lg">
                          <div className="text-neutral-600 mb-2">
                            <i className="fa-solid fa-exclamation-circle text-xl"></i>
                          </div>
                          <h3 className="text-neutral-700 font-medium text-sm mb-1">
                            Unable to load rooms
                          </h3>
                          <p className="text-neutral-600 text-xs text-center font-light">
                            We couldn't load room information at this time.
                            Please try again later.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="flex flex-row items-start justify-between">
                            <div className="top-0 z-10 w-full">
                              <div className="flex flex-row items-center justify-between">
                                <div className="flex flex-col items-start justify-between space-y-1">
                                  <h2 className="text-[15px] font-semibold text-neutral-800">
                                    Available Rooms
                                  </h2>
                                  <div className="flex items-center gap-3 text-neutral-700">
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-[13px] font-light leading-relaxed">
                                        {
                                          roomDetails?.filter(
                                            (room: any) =>
                                              room.images.length > 0
                                          ).length
                                        }{" "}
                                        rooms available
                                      </span>
                                      <div className="w-0.5 h-0.5 bg-neutral-500 rounded-full"></div>
                                      <span className="text-[13px] font-light leading-relaxed">
                                        Prices per night
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex gap-2 ml-4">
                                  <button
                                    onClick={() => scroll("left")}
                                    disabled={!canScrollLeft}
                                    className={`p-1.5 rounded-full border border-neutral-200 transition-all duration-300 ${
                                      canScrollLeft
                                        ? "hover:border-neutral-600 text-neutral-600"
                                        : "text-neutral-300 cursor-not-allowed"
                                    }`}
                                  >
                                    <ChevronLeft className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => scroll("right")}
                                    disabled={!canScrollRight}
                                    className={`p-1.5 rounded-full border border-neutral-200 transition-all duration-300 ${
                                      canScrollRight
                                        ? "hover:border-neutral-600 text-neutral-600"
                                        : "text-neutral-300 cursor-not-allowed"
                                    }`}
                                  >
                                    <ChevronRight className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="relative overflow-hidden">
                            <div
                              ref={scrollContainerRef}
                              className="flex flex-row overflow-x-auto rounded-lg no-scrollbar scroll-smooth w-full"
                              style={{ scrollSnapType: "x mandatory" }}
                            >
                              {roomDetails
                                ?.filter((room: any) => room.images.length > 0)
                                .map((room: any) => (
                                  <div
                                    key={room.id}
                                    className="flex-none w-1/2 snap-start pr-2 box-border"
                                    style={{ scrollSnapAlign: "start" }}
                                  >
                                    <div className="flex flex-col gap-2">
                                      <div className="relative w-full h-[10rem] overflow-hidden rounded-lg">
                                        <LazyLoad
                                          src={formatImageUrl(
                                            room.images[0],
                                            "medium"
                                          )}
                                          lowResSrc={formatImageUrl(
                                            room.images[0],
                                            "low"
                                          )}
                                          alt={room.name}
                                          className="w-full h-full object-cover"
                                        />
                                        <div className="absolute top-2 right-2 bg-white/80 backdrop-blur-sm rounded-md px-3 py-1.5 flex flex-row items-center gap-2">
                                          <span className="text-xs font-light">
                                            {formatPrice(
                                              room.price.currency,
                                              room.price.total /
                                                (location.date.num_days - 1)
                                            )}
                                          </span>
                                        </div>
                                      </div>
                                      <div className="w-full flex flex-row items-center justify-between gap-2">
                                        <div className="flex flex-col gap-1 w-[70%]">
                                          <h4 className="font-medium text-neutral-800 text-[13px] truncate w-full">
                                            {room.name}
                                          </h4>
                                          <div className="flex flex-row items-center gap-2">
                                            {/* Occupancy */}
                                            <div className="flex items-center gap-2 text-neutral-600 font-light text-sm">
                                              <UserIcon className="w-3.5 h-3.5" />
                                              <span className="text-xs line-clamp-1">
                                                {room.occupancy.maxOccupancy}
                                              </span>
                                            </div>
                                            <div className="w-0.5 h-0.5 bg-neutral-600 rounded-full"></div>
                                            {/* bed types */}
                                            <div className="flex items-center gap-2 text-neutral-600 font-light text-sm">
                                              <BedIcon className="w-3.5 h-3.5" />
                                              {room.bedTypes
                                                .slice(0, 1)
                                                .map(
                                                  (bed: { name: string }) => (
                                                    <span
                                                      key={bed.name}
                                                      className="text-xs line-clamp-1"
                                                    >
                                                      {formattedBedType(
                                                        room.bedTypes
                                                      )}{" "}
                                                    </span>
                                                  )
                                                )}
                                            </div>
                                          </div>
                                        </div>
                                        <button
                                          onClick={() => handleViewRoom(room)}
                                          className="text-xs text-neutral-600 hover:text-neutral-600 border border-neutral-200 hover:border-neutral-600 px-3 py-1.5 rounded-full transition-all duration-300"
                                        >
                                          View
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </motion.div>

      {/* Add Facilities Modal */}
      {showFacilities && (
        <FacilitiesModal
          hotelName={lodging.name}
          isOpen={showFacilities}
          setIsOpen={setShowFacilities}
          facilities={lodgingDetails?.facilities || []}
        />
      )}

      {/* Room Details Modal */}
      {selectedRoom && (
        <RoomDetailsModal
          room={selectedRoom}
          isOpen={!!selectedRoom}
          onClose={handleCloseRoomDetails}
          numNights={location.date.num_days - 1}
          onBookNow={() => handleAddRoom(selectedRoom)}
        />
      )}
    </>
  );
};

export default MainLodgingModal;
